import React, { use, useEffect } from "react";
import styled from "styled-components";
import InputField from "./core-components/InputField.tsx";
import Button from "./core-components/Button.tsx";
import { ToastContainer, toast } from "react-toastify";

import {
  Page,
  Text,
  View,
  Document,
  PDFViewer,
  PDFDownloadLink,
  Image,
  pdf,
} from "@react-pdf/renderer";
import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import { orderTaxes, orderTaxesNames } from "../../utils/constants.ts";
import { sendEmail, createOrderInvoice } from "../../services/orderService.js";

const FormContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;

  align-self: center;
  margin-bottom: 8px;
`;

interface Props {
  order: any;
  token?: string;
  refetchOrder?: () => void;
  isManual?: boolean;
}

const InvoiceComponent: React.FC<Props> = ({
  order,
  token,
  refetchOrder,
  isManual,
}) => {
  const handleInvoiceSave = async () => {
    try {
      await createOrderInvoice(order.orderID, token);

      refetchOrder && refetchOrder();
    } catch (error) {
      notifyErr();
      console.error("Error creating invoice:", error);
    }
  };

  const getTaxes = () => {
    let total = 0;
    if (order.shippingOption === orderTaxesNames.shipping)
      total += orderTaxes.shipping;
    if (order.paymentOption === orderTaxesNames.cash) total += orderTaxes.cash;

    return total;
  };

  const notify = () =>
    toast.success("Factură trimisă către client !", {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "light",
    });

  const notifyErr = () =>
    toast.error("Factura nu s-a putut trimite. Incearcă mai târziu !", {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "light",
    });

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result.split(",")[1]); // elimină prefixul base64
        } else {
          reject("Eroare: reader.result nu este un string.");
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleSend = async () => {
    const blob = await pdf(<InvoicePDF />).toBlob();
    const base64 = await blobToBase64(blob);
    const mail = {
      subject: "Factura comanda #" + order?.orderID,
      toAddress: isManual ? "contact@filoprint.ro" : order?.email,
      content: `<div>
                  <h3> Mulțumim că ai cumpărat de la noi ! </h3>
                  <p>
                   În acest email găsești atașata factura pentru comanda #${order?.orderID}.
                  </p>
                  <p>Dacă ești și tu la fel de încântat ca noi de produse, te rugăm să lași o recenzie pe site.</p>
                  <p>Te mai așteptăm, FiloPrint !</p>
                  <img
                    src="https://filoprint.ro/images/header-logo.png"
                    alt="Logo"
                    width="100"
                  />
                  <div>Telefon: 0724702368</div>
                  <div>
                    Site: https://filoprint.ro/
                  </div>
                </div>
          `,
      attachment: {
        base64Content: base64,
        attachmentName: "factura.pdf",
      },
    };

    try {
      await sendEmail(mail);
      notify();
    } catch (error) {
      console.error("Error sending email", error);
      notifyErr();
    }
  };

  useEffect(() => {
    if (isManual) {
      handleSend();
    }
  }, [isManual]);

  const InvoicePDF = () => (
    <Document>
      <Page
        size="A4"
        style={{
          backgroundColor: "#fff",
          color: "#262626",
          fontFamily: "Helvetica",
          fontSize: 12,
          padding: 30,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <View>
            <Image
              src="/images/header-logo.png"
              style={{ width: 100, height: "auto" }}
            />
            <Text style={{ fontSize: 16, marginBottom: 8, fontWeight: "bold" }}>
              FACTURA nr. A
              {(order.invoice ? order.invoice.invoiceID : "0000")
                .toString()
                .padStart(4, "0")}
            </Text>
            <Text>
              Data emiterii:{" "}
              {order.invoice
                ? order.invoice.invoiceDate
                : new Date().toLocaleDateString("ro-RO")}
            </Text>
          </View>
          <View
            style={{
              display: "flex",

              flexDirection: "column",
              gap: 4,
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                borderBottom: "1px solid #659c47",
                marginBottom: 8,
              }}
            >
              Furnizor
            </Text>
            <Text
              style={{
                fontWeight: "bold",
                marginBottom: 2,
              }}
            >
              FILOPRINT SRL
            </Text>
            <Text>CIF: 43031922</Text>
            <Text>Reg. com: J15/937/10.09.2020</Text>
            <Text>Adresa: Strada Liliacului, Nr. 9, Doicesti, Dâmbovita</Text>
            <Text>IBAN: RO86BTRLRONCRT0566636801</Text>
            <Text>Banca: Banca Transilvania</Text>
          </View>
        </View>

        <View style={{ marginBottom: 20 }}>
          <Text
            style={{
              fontWeight: "bold",
              borderBottom: "1px solid #659c47",
              marginBottom: 8,
              width: "50%",
            }}
          >
            Client
          </Text>
          <Text
            style={{
              fontWeight: "bold",
              marginBottom: 2,
            }}
          >
            {order.firstName} {order.lastName}
          </Text>
          <Text>
            Adresa: {order.street}, {order.city}, {order.county}, Romania
          </Text>
        </View>

        <Table
          style={{
            display: "table",
            width: "auto",
            borderStyle: "solid",
            borderWidth: 1,
            borderRightWidth: 0,
            borderBottomWidth: 0,
            marginBottom: 16,
          }}
        >
          {/* Header */}
          <TH style={{ flexDirection: "row" }}>
            {["Denumire", "Cantitate", "Pret Unitar", "Total"].map(
              (title, idx, arr) => (
                <TD
                  key={idx}
                  style={{
                    padding: 8,
                    fontSize: 10,
                    fontWeight: "bold",
                    backgroundColor: "#e5eddd",
                    borderLeftWidth: 0,
                    borderRightWidth: 0,
                    flex: 1,
                  }}
                >
                  {title}
                </TD>
              )
            )}
          </TH>

          {order.orderDetails.map((item, index) => (
            <TR style={{ flexDirection: "row" }} key={index}>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                {item.productName}
              </TD>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                {item.productCantity}
              </TD>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                {item.salePrice} lei
              </TD>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderRightWidth: 0,
                  borderTopWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                {item.salePrice * item.productCantity} lei
              </TD>
            </TR>
          ))}
          {getTaxes() > 0 && (
            <TR style={{ flexDirection: "row" }}>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                Taxe de livrare
              </TD>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                1
              </TD>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                {getTaxes()} lei
              </TD>
              <TD
                style={{
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderLeftWidth: 0,
                  borderRightWidth: 0,
                  borderTopWidth: 0,
                  padding: 8,
                  fontSize: 10,
                  flex: 1,
                }}
              >
                {getTaxes()} lei
              </TD>
            </TR>
          )}
        </Table>

        <View
          style={{
            marginTop: 24,
            minWidth: "256px",
          }}
        >
          <Text style={{ fontWeight: "bold", color: "#659c47" }}>
            TOTAL PLATA: {order.totalPrice} lei
          </Text>
        </View>
      </Page>
    </Document>
  );

  return (
    <FormContainer>
      {order.invoice === null ? (
        <>
          <p style={{ margin: "0", fontSize: "14px" }}>
            Acesta este un preview. Numărul facturii se va genera automat după
            salvare.
          </p>
          <Button
            id="save-invoice"
            onClick={handleInvoiceSave}
            text="Generează"
            backgroundColor={"var(--primary-brown)"}
            color="white"
          />
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "row", gap: 16 }}>
          <PDFDownloadLink document={<InvoicePDF />} fileName="factura.pdf">
            <button
              style={{
                marginTop: "16px",
                backgroundColor: "var(--primary-brown)",
                color: "white",
                padding: "8px 16px",
                border: "none",
                cursor: "pointer",
                borderRadius: "4px",
              }}
            >
              Descărcați factura
            </button>
          </PDFDownloadLink>
          {!isManual && (
            <button
              onClick={handleSend}
              style={{
                marginTop: "16px",
                backgroundColor: "var(--primary-brown)",
                color: "white",
                padding: "8px 16px",
                border: "none",
                cursor: "pointer",
                borderRadius: "4px",
              }}
            >
              Trimite factura catre client
            </button>
          )}
        </div>
      )}
      <div style={{ width: "100%", height: "65vh" }}>
        <div style={{ width: "50vw", height: "70vh" }}>
          <PDFViewer width="100%" height="100%">
            <InvoicePDF />
          </PDFViewer>
        </div>
      </div>

      <ToastContainer />
    </FormContainer>
  );
};

export default InvoiceComponent;
